import { queryKeys } from "@l2a/shared";
import { CalendarDays, Columns3, ListChecks, ListTodo, Repeat, TriangleAlert } from "lucide-react";
import { Suspense, lazy } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

// React.lazy so the settings page costs the entry bundle nothing but its icon.
const TaskSettingsPage = lazy(() => import("./settings"));

/** The one key the badge reads and `taskKeys.myWork(null)` writes. */
const MY_WORK_BADGE_KEY = queryKeys.list("task", { scope: "my-work", userId: null });

/**
 * Tasks — every task across every project, as a board, a grouped list or a
 * calendar, plus **My Work** and the recurring rules that create work on a
 * schedule. PRD §7 Item 3 (US-3.2, US-3.3, US-3.4, US-3.7).
 *
 * My Work lives here rather than in a feature of its own because it is the same
 * domain seen from one person's side: the same DTOs, the same drawer, the same
 * cache entries. A separate feature would have had to reach into this one's
 * internals, which the frontend conventions (§1.5) forbid for good reason.
 *
 * `/tasks/:id` is fixed by ENTITY_ROUTES: search results, notifications and
 * e-mails link there, so the route must open that exact task.
 */
export default defineFeature({
  key: "tasks",
  title: "Tasks",
  entities: ["task"],
  routes: [
    { path: "my-work", lazy: () => import("./my-work") },
    { path: "tasks", lazy: () => import("./page") },
    // Static before dynamic: /tasks/recurring must not be read as a task id.
    { path: "tasks/recurring", lazy: () => import("./recurring") },
    { path: "tasks/:id", lazy: () => import("./page") },
  ],
  nav: [
    {
      key: "my-work",
      label: "My work",
      to: "/my-work",
      icon: ListTodo,
      group: "home",
      order: 30,
      // READS the cache entry the My Work page itself writes — the sidebar
      // never fetches, so this key and `taskKeys.myWork(null)` must match.
      badgeQueryKey: MY_WORK_BADGE_KEY,
      badgeTone: "danger",
      description: "Your tasks, overdue first, across every project",
    },
    {
      label: "Tasks",
      to: "/tasks",
      icon: ListChecks,
      group: "delivery",
      order: 20,
      permission: "projects:read",
      description: "Every task across every project",
    },
  ],
  quickCreate: [
    {
      key: "task",
      label: "Task",
      icon: ListChecks,
      to: "/tasks?new=1",
      permission: "tasks:write",
      order: 30,
      shortcut: "t",
      description: "Assign work with a due date",
    },
  ],
  commands: [
    { key: "tasks.board", label: "Task board", icon: Columns3, to: "/tasks", group: "navigate", permission: "projects:read", keywords: ["kanban", "columns"] },
    { key: "tasks.calendar", label: "Task calendar", icon: CalendarDays, to: "/tasks?view=calendar", group: "navigate", permission: "projects:read", keywords: ["due dates", "month"] },
    {
      key: "tasks.overdue",
      label: "Show overdue tasks",
      icon: TriangleAlert,
      to: "/tasks?view=list&groupBy=assignee&overdue=true",
      group: "actions",
      permission: "projects:read",
      keywords: ["late", "past due"],
    },
    {
      key: "tasks.unassigned",
      label: "Show unassigned tasks",
      icon: ListChecks,
      to: "/tasks?view=list&unassigned=true",
      group: "actions",
      permission: "projects:read",
      keywords: ["nobody", "owner"],
    },
    { key: "tasks.recurring", label: "Recurring tasks", icon: Repeat, to: "/tasks/recurring", group: "navigate", permission: "projects:read", keywords: ["repeat", "schedule", "every month"] },
    { key: "mywork", label: "My work", icon: ListTodo, to: "/my-work", group: "navigate", keywords: ["my tasks", "today", "overdue", "due"] },
  ],
  settingsSections: [
    {
      key: "task-statuses",
      label: "Tasks",
      path: "tasks",
      group: "Delivery",
      order: 120,
      icon: ListChecks,
      description: "Board columns and the non-working days recurring tasks step over",
      permission: "projects:write",
      element: (
        <Suspense fallback={<Skeleton className="h-72 w-full" rounded="card" />}>
          <TaskSettingsPage />
        </Suspense>
      ),
    },
  ],
});

